import {
    type ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowClockwiseIcon, InfoIcon } from '@phosphor-icons/react';
import { EngineService } from '@/services/engine/engine-service';
import type { HdmiSetupPayload } from '@/types/hdmi';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getApiErrorMessage } from '@/utils/error-messages';
import LocalStorage from '@/utils/local-storage';
import { isCanceledError, readErrorBlobText } from '@/utils/http';

const PREVIEW_DEBOUNCE_MS = 400;
const SNAPSHOT_DEBOUNCE_MS = 800;
const HDMI_SETUP_STORAGE_KEY = 'setup-hdmi-form-values';

type CropValues = Readonly<{
    left: number;
    top: number;
    right: number;
    bottom: number;
}>;
type PaddingValues = Readonly<{
    left: number;
    top: number;
    right: number;
    bottom: number;
}>;
type SetupHdmiStoredValues = Readonly<{
    manualDevice: string;
    inputWidth: number;
    inputHeight: number;
    rate: number;
    gridColumns: number;
    gridRows: number;
    viewportWidth: number;
    viewportHeight: number;
    crop: CropValues;
    padding: PaddingValues;
}>;

const DEFAULT_SETUP_HDMI_VALUES: SetupHdmiStoredValues = {
    manualDevice: '',
    inputWidth: 1280,
    inputHeight: 720,
    rate: 30,
    gridColumns: 2,
    gridRows: 2,
    viewportWidth: 640,
    viewportHeight: 360,
    crop: {
        left: 0,
        top: 0,
        right: 1,
        bottom: 1,
    },
    padding: {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
    },
};

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function toIntegerOr(
    value: unknown,
    fallback: number,
    minimum: number,
): number {
    return isFiniteNumber(value) && Number.isInteger(value) && value >= minimum
        ? value
        : fallback;
}

function clampNumber(value: unknown, fallback: number): number {
    return isFiniteNumber(value) ? Math.max(0, Math.min(1, value)) : fallback;
}

function toStringOr(value: unknown, fallback: string): string {
    return typeof value === 'string' ? value : fallback;
}

function toRecord(value: unknown): Record<string, unknown> {
    return typeof value === 'object' && value !== null
        ? (value as Record<string, unknown>)
        : {};
}

function loadSetupHdmiStoredValues(): SetupHdmiStoredValues {
    const storedValue = LocalStorage.get<unknown>(HDMI_SETUP_STORAGE_KEY);
    const storedRecord = toRecord(storedValue);
    const storedCrop = toRecord(storedRecord.crop);
    const storedPadding = toRecord(storedRecord.padding);

    return {
        manualDevice: toStringOr(
            storedRecord.manualDevice,
            DEFAULT_SETUP_HDMI_VALUES.manualDevice,
        ),
        inputWidth: toIntegerOr(
            storedRecord.inputWidth,
            DEFAULT_SETUP_HDMI_VALUES.inputWidth,
            1,
        ),
        inputHeight: toIntegerOr(
            storedRecord.inputHeight,
            DEFAULT_SETUP_HDMI_VALUES.inputHeight,
            1,
        ),
        rate: toIntegerOr(storedRecord.rate, DEFAULT_SETUP_HDMI_VALUES.rate, 1),
        gridColumns: toIntegerOr(
            storedRecord.gridColumns,
            DEFAULT_SETUP_HDMI_VALUES.gridColumns,
            1,
        ),
        gridRows: toIntegerOr(
            storedRecord.gridRows,
            DEFAULT_SETUP_HDMI_VALUES.gridRows,
            1,
        ),
        viewportWidth: toIntegerOr(
            storedRecord.viewportWidth,
            DEFAULT_SETUP_HDMI_VALUES.viewportWidth,
            1,
        ),
        viewportHeight: toIntegerOr(
            storedRecord.viewportHeight,
            DEFAULT_SETUP_HDMI_VALUES.viewportHeight,
            1,
        ),
        crop: {
            left: clampNumber(
                storedCrop.left,
                DEFAULT_SETUP_HDMI_VALUES.crop.left,
            ),
            top: clampNumber(
                storedCrop.top,
                DEFAULT_SETUP_HDMI_VALUES.crop.top,
            ),
            right: clampNumber(
                storedCrop.right,
                DEFAULT_SETUP_HDMI_VALUES.crop.right,
            ),
            bottom: clampNumber(
                storedCrop.bottom,
                DEFAULT_SETUP_HDMI_VALUES.crop.bottom,
            ),
        },
        padding: {
            left: clampNumber(
                storedPadding.left,
                DEFAULT_SETUP_HDMI_VALUES.padding.left,
            ),
            top: clampNumber(
                storedPadding.top,
                DEFAULT_SETUP_HDMI_VALUES.padding.top,
            ),
            right: clampNumber(
                storedPadding.right,
                DEFAULT_SETUP_HDMI_VALUES.padding.right,
            ),
            bottom: clampNumber(
                storedPadding.bottom,
                DEFAULT_SETUP_HDMI_VALUES.padding.bottom,
            ),
        },
    };
}

function isValidDevicePath(device: string): boolean {
    return device.startsWith('/dev/');
}

function buildPayload(
    device: string,
    inputWidth: number,
    inputHeight: number,
    rate: number,
    gridColumns: number,
    gridRows: number,
    viewportWidth: number,
    viewportHeight: number,
    crop: CropValues,
    padding: PaddingValues,
): HdmiSetupPayload {
    return {
        device,
        input_width: inputWidth,
        input_height: inputHeight,
        rate,
        grid_columns: gridColumns,
        grid_rows: gridRows,
        viewport_width: viewportWidth,
        viewport_height: viewportHeight,
        crop_left: crop.left,
        crop_top: crop.top,
        crop_right: crop.right,
        crop_bottom: crop.bottom,
        padding_left: padding.left,
        padding_top: padding.top,
        padding_right: padding.right,
        padding_bottom: padding.bottom,
    };
}

export function SetupHdmi() {
    const { t } = useTranslation(I18nNamespace.SETUP_HDMI);
    const [storedValues] = useState<SetupHdmiStoredValues>(
        loadSetupHdmiStoredValues,
    );

    const { data: clusterState, mutate: mutateCluster } = useSWR(
        'hdmi-cluster',
        () => EngineService.getHdmiCluster(),
        { revalidateOnFocus: false },
    );

    const { data: v4lDevices, error: devicesError } = useSWR(
        'hdmi-v4l-devices',
        () => EngineService.listV4lDevices(),
        { revalidateOnFocus: false },
    );

    const [devicePath, setDevicePath] = useState('');
    const [manualDevice, setManualDevice] = useState(storedValues.manualDevice);
    const [inputWidth, setInputWidth] = useState(storedValues.inputWidth);
    const [inputHeight, setInputHeight] = useState(storedValues.inputHeight);
    const [rate, setRate] = useState(storedValues.rate);
    const [gridColumns, setGridColumns] = useState(storedValues.gridColumns);
    const [gridRows, setGridRows] = useState(storedValues.gridRows);
    const [viewportWidth, setViewportWidth] = useState(
        storedValues.viewportWidth,
    );
    const [viewportHeight, setViewportHeight] = useState(
        storedValues.viewportHeight,
    );
    const [crop, setCrop] = useState<CropValues>(storedValues.crop);
    const [padding, setPadding] = useState<PaddingValues>(storedValues.padding);

    const effectiveDevice = manualDevice.trim() || devicePath;

    const { data: formats, error: formatsError } = useSWR(
        isValidDevicePath(effectiveDevice)
            ? ['hdmi-formats', effectiveDevice]
            : null,
        () => EngineService.listV4lFormats(effectiveDevice),
        { revalidateOnFocus: false },
    );

    const previewPayload = useMemo(
        () =>
            buildPayload(
                effectiveDevice,
                inputWidth,
                inputHeight,
                rate,
                gridColumns,
                gridRows,
                viewportWidth,
                viewportHeight,
                crop,
                padding,
            ),
        [
            effectiveDevice,
            inputWidth,
            inputHeight,
            rate,
            gridColumns,
            gridRows,
            viewportWidth,
            viewportHeight,
            crop,
            padding,
        ],
    );

    const debouncedPreviewPayload = useDebounce(
        previewPayload,
        PREVIEW_DEBOUNCE_MS,
    );

    const { data: preview = [], error: previewErrorRaw } = useSWR(
        isValidDevicePath(debouncedPreviewPayload.device)
            ? ['hdmi-preview', debouncedPreviewPayload]
            : null,
        () => EngineService.previewHdmiViewports(debouncedPreviewPayload),
        { revalidateOnFocus: false },
    );
    const previewError = previewErrorRaw
        ? getApiErrorMessage(previewErrorRaw)
        : null;

    const [isApplying, setIsApplying] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
    const [snapshotLoading, setSnapshotLoading] = useState(false);
    const [snapshotError, setSnapshotError] = useState<string | null>(null);
    const snapshotBlobRef = useRef<string | null>(null);
    const snapshotAbortRef = useRef<AbortController | null>(null);

    const snapshotKey = useMemo(
        () => ({
            device: effectiveDevice,
            w: inputWidth,
            h: inputHeight,
            r: rate,
        }),
        [effectiveDevice, inputWidth, inputHeight, rate],
    );
    const debouncedSnapshotKey = useDebounce(snapshotKey, SNAPSHOT_DEBOUNCE_MS);

    const captureSnapshot = useCallback(
        async (device: string, width: number, height: number, fps: number) => {
            if (
                !isValidDevicePath(device) ||
                width <= 0 ||
                height <= 0 ||
                fps <= 0
            ) {
                return;
            }
            snapshotAbortRef.current?.abort();
            const controller = new AbortController();
            snapshotAbortRef.current = controller;
            setSnapshotLoading(true);
            setSnapshotError(null);
            try {
                const url = EngineService.hdmiSnapshotUrl(
                    device,
                    width,
                    height,
                    fps,
                );
                const blob = await EngineService.fetchImageBlob(
                    url,
                    controller.signal,
                );
                if (snapshotBlobRef.current) {
                    URL.revokeObjectURL(snapshotBlobRef.current);
                }
                const objectUrl = URL.createObjectURL(blob);
                snapshotBlobRef.current = objectUrl;
                setSnapshotUrl(objectUrl);
            } catch (e) {
                if (isCanceledError(e)) return;
                const detail = await readErrorBlobText(e);
                setSnapshotError(detail || getApiErrorMessage(e));
            } finally {
                setSnapshotLoading(false);
            }
        },
        [],
    );

    const isConfigured = clusterState?.configured === true;

    useEffect(() => {
        if (isConfigured) return;
        void captureSnapshot(
            debouncedSnapshotKey.device,
            debouncedSnapshotKey.w,
            debouncedSnapshotKey.h,
            debouncedSnapshotKey.r,
        );
    }, [debouncedSnapshotKey, captureSnapshot, isConfigured]);

    useEffect(() => {
        return () => {
            snapshotAbortRef.current?.abort();
            if (snapshotBlobRef.current) {
                URL.revokeObjectURL(snapshotBlobRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!v4lDevices?.length || manualDevice.trim() || devicePath) {
            return;
        }
        setDevicePath(v4lDevices[0].path);
    }, [v4lDevices, devicePath, manualDevice]);

    useEffect(() => {
        LocalStorage.set(HDMI_SETUP_STORAGE_KEY, {
            manualDevice,
            inputWidth,
            inputHeight,
            rate,
            gridColumns,
            gridRows,
            viewportWidth,
            viewportHeight,
            crop,
            padding,
        } satisfies SetupHdmiStoredValues);
    }, [
        manualDevice,
        inputWidth,
        inputHeight,
        rate,
        gridColumns,
        gridRows,
        viewportWidth,
        viewportHeight,
        crop,
        padding,
    ]);

    const applyDisabled =
        isConfigured ||
        isApplying ||
        isRemoving ||
        !isValidDevicePath(effectiveDevice) ||
        inputWidth <= 0 ||
        inputHeight <= 0 ||
        rate <= 0 ||
        gridColumns < 1 ||
        gridRows < 1 ||
        viewportWidth <= 0 ||
        viewportHeight <= 0;

    const handleApply = async () => {
        if (applyDisabled) {
            return;
        }
        setIsApplying(true);
        try {
            await EngineService.applyHdmi(previewPayload);
            toast.success(t('toast.applySuccess'));
            await mutateCluster();
        } catch (e) {
            toast.error(getApiErrorMessage(e, t('errors.applyFailed')));
        } finally {
            setIsApplying(false);
        }
    };

    const handleRemove = async () => {
        if (isRemoving || isApplying) {
            return;
        }
        setIsRemoving(true);
        try {
            await EngineService.removeHdmi();
            toast.success(t('toast.removeSuccess'));
            await mutateCluster();
        } catch (e) {
            toast.error(getApiErrorMessage(e, t('errors.removeFailed')));
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-8 px-4 pb-32 pt-6 text-grey-100 md:px-6">
            <div>
                <h1 className="font-geist text-2xl font-semibold tracking-tight text-grey-50">
                    {t('title')}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-grey-400">
                    {t('intro')}
                </p>
            </div>

            {clusterState?.configured ? (
                <div className="rounded-lg border border-brand-700/40 bg-brand-950/30 px-4 py-3 text-sm text-grey-200">
                    {t('status.configured')}
                    <Button
                        type="button"
                        variant="danger"
                        className="ml-4 inline-flex"
                        disabled={isRemoving || isApplying}
                        onClick={() => void handleRemove()}
                    >
                        {t('actions.remove')}
                    </Button>
                </div>
            ) : null}

            {devicesError ? (
                <p className="text-sm text-red-400">
                    {t('errors.devicesFailed')}
                </p>
            ) : null}

            <section className="grid gap-6 md:grid-cols-2">
                <div className="flex flex-col gap-4">
                    <div>
                        <Label htmlFor="hdmi-device-select">
                            {t('fields.device')}
                        </Label>
                        <select
                            id="hdmi-device-select"
                            className="mt-1.5 flex h-10 w-full rounded-md border border-grey-700 bg-grey-900 px-3 text-sm text-grey-100"
                            value={devicePath}
                            onChange={(event) =>
                                setDevicePath(event.target.value)
                            }
                        >
                            {(v4lDevices ?? []).length === 0 ? (
                                <option value="">{t('empty.devices')}</option>
                            ) : null}
                            {(v4lDevices ?? []).map((d) => (
                                <option key={d.path} value={d.path}>
                                    {d.label} — {d.path}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label htmlFor="hdmi-manual-device">
                            {t('fields.manualDevice')}
                        </Label>
                        <Input
                            id="hdmi-manual-device"
                            className="mt-1.5"
                            placeholder="/dev/v4l/by-id/..."
                            value={manualDevice}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setManualDevice(event.target.value)
                            }
                        />
                        <p className="mt-1 text-xs text-grey-400">
                            {t('hints.manualDevice')}
                        </p>
                    </div>

                    {formatsError ? (
                        <p className="text-xs text-amber-400">
                            {t('errors.formatsFailed')}
                        </p>
                    ) : null}

                    {!formatsError &&
                    formats &&
                    formats.length === 0 &&
                    effectiveDevice.length > 3 ? (
                        <p className="text-xs text-grey-400">
                            {t('hints.noFormats')}
                        </p>
                    ) : null}

                    {formats && formats.length > 0 ? (
                        <div>
                            <Label>{t('fields.supportedFormats')}</Label>
                            <div className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-grey-800">
                                <table className="w-full text-left text-xs text-grey-300">
                                    <thead className="sticky top-0 bg-grey-900 text-grey-400">
                                        <tr>
                                            <th className="px-2 py-1">
                                                {t('table.width')}
                                            </th>
                                            <th className="px-2 py-1">
                                                {t('table.height')}
                                            </th>
                                            <th className="px-2 py-1">
                                                {t('table.fps')}
                                            </th>
                                            <th className="px-2 py-1" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {formats.map((row, index) => (
                                            <tr
                                                key={`${row.width}x${row.height}-${row.fps}-${index}`}
                                                className="border-t border-grey-800"
                                            >
                                                <td className="px-2 py-1">
                                                    {row.width}
                                                </td>
                                                <td className="px-2 py-1">
                                                    {row.height}
                                                </td>
                                                <td className="px-2 py-1">
                                                    {row.fps}
                                                </td>
                                                <td className="px-2 py-1">
                                                    <button
                                                        type="button"
                                                        className="text-brand-400 hover:underline"
                                                        onClick={() => {
                                                            setInputWidth(
                                                                row.width,
                                                            );
                                                            setInputHeight(
                                                                row.height,
                                                            );
                                                            setRate(row.fps);
                                                        }}
                                                    >
                                                        {t('actions.useFormat')}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="flex flex-col gap-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="input-w">
                                {t('fields.inputWidth')}
                            </Label>
                            <Input
                                id="input-w"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={inputWidth}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setInputWidth(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 0,
                                    )
                                }
                            />
                        </div>
                        <div>
                            <Label htmlFor="input-h">
                                {t('fields.inputHeight')}
                            </Label>
                            <Input
                                id="input-h"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={inputHeight}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setInputHeight(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 0,
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="rate">{t('fields.rate')}</Label>
                        <Input
                            id="rate"
                            type="number"
                            min={1}
                            className="mt-1.5 max-w-[120px]"
                            value={rate}
                            onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                setRate(
                                    Number.parseInt(event.target.value, 10) ||
                                        0,
                                )
                            }
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="grid-c">
                                {t('fields.gridColumns')}
                            </Label>
                            <Input
                                id="grid-c"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={gridColumns}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setGridColumns(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 1,
                                    )
                                }
                            />
                        </div>
                        <div>
                            <Label htmlFor="grid-r">
                                {t('fields.gridRows')}
                            </Label>
                            <Input
                                id="grid-r"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={gridRows}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setGridRows(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 1,
                                    )
                                }
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label htmlFor="vp-w">
                                {t('fields.viewportWidth')}
                            </Label>
                            <Input
                                id="vp-w"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={viewportWidth}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setViewportWidth(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 0,
                                    )
                                }
                            />
                        </div>
                        <div>
                            <Label htmlFor="vp-h">
                                {t('fields.viewportHeight')}
                            </Label>
                            <Input
                                id="vp-h"
                                type="number"
                                min={1}
                                className="mt-1.5"
                                value={viewportHeight}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>,
                                ) =>
                                    setViewportHeight(
                                        Number.parseInt(
                                            event.target.value,
                                            10,
                                        ) || 0,
                                    )
                                }
                            />
                        </div>
                    </div>
                </div>
            </section>

            <details className="group rounded-lg border border-grey-700 bg-grey-900/30">
                <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-grey-200 transition-colors hover:text-grey-100">
                    {t('sections.advanced')}
                </summary>
                <section className="grid gap-6 px-4 pb-5 pt-2 md:grid-cols-2">
                    <div className="flex flex-col gap-3">
                        <div>
                            <h3 className="text-sm font-medium">
                                {t('sections.crop')}
                            </h3>
                            <p className="mt-1 text-xs text-grey-400">
                                {t('hints.crop')}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="crop-l">
                                    {t('fields.cropLeft')}
                                </Label>
                                <Input
                                    id="crop-l"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(crop.left * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCrop((prev) => ({
                                            ...prev,
                                            left:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="crop-t">
                                    {t('fields.cropTop')}
                                </Label>
                                <Input
                                    id="crop-t"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(crop.top * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCrop((prev) => ({
                                            ...prev,
                                            top:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="crop-r">
                                    {t('fields.cropRight')}
                                </Label>
                                <Input
                                    id="crop-r"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(crop.right * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCrop((prev) => ({
                                            ...prev,
                                            right:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="crop-b">
                                    {t('fields.cropBottom')}
                                </Label>
                                <Input
                                    id="crop-b"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(crop.bottom * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setCrop((prev) => ({
                                            ...prev,
                                            bottom:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <div>
                            <h3 className="text-sm font-medium">
                                {t('sections.padding')}
                            </h3>
                            <p className="mt-1 text-xs text-grey-400">
                                {t('hints.padding')}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="pad-l">
                                    {t('fields.paddingLeft')}
                                </Label>
                                <Input
                                    id="pad-l"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(padding.left * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setPadding((prev) => ({
                                            ...prev,
                                            left:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="pad-t">
                                    {t('fields.paddingTop')}
                                </Label>
                                <Input
                                    id="pad-t"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(padding.top * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setPadding((prev) => ({
                                            ...prev,
                                            top:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="pad-r">
                                    {t('fields.paddingRight')}
                                </Label>
                                <Input
                                    id="pad-r"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(padding.right * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setPadding((prev) => ({
                                            ...prev,
                                            right:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <Label htmlFor="pad-b">
                                    {t('fields.paddingBottom')}
                                </Label>
                                <Input
                                    id="pad-b"
                                    type="number"
                                    step={1}
                                    min={0}
                                    max={100}
                                    className="mt-1.5"
                                    value={Math.round(padding.bottom * 100)}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>,
                                    ) =>
                                        setPadding((prev) => ({
                                            ...prev,
                                            bottom:
                                                (parseInt(
                                                    event.target.value,
                                                    10,
                                                ) || 0) / 100,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </details>

            <section>
                <div className="flex items-center gap-3">
                    <h2 className="font-geist text-lg font-medium text-grey-100">
                        {t('preview.title')}
                    </h2>
                    {!isConfigured && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="inline-flex">
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded p-1 text-grey-400 transition-colors hover:bg-grey-800 hover:text-grey-100 disabled:opacity-40"
                                        disabled={
                                            snapshotLoading ||
                                            !isValidDevicePath(effectiveDevice)
                                        }
                                        aria-label={t(
                                            'actions.refreshSnapshot',
                                        )}
                                        onClick={() =>
                                            void captureSnapshot(
                                                effectiveDevice,
                                                inputWidth,
                                                inputHeight,
                                                rate,
                                            )
                                        }
                                    >
                                        <ArrowClockwiseIcon
                                            size={16}
                                            className={
                                                snapshotLoading
                                                    ? 'animate-spin'
                                                    : ''
                                            }
                                        />
                                    </button>
                                </span>
                            </TooltipTrigger>
                            <TooltipContent>
                                {t('actions.refreshSnapshot')}
                            </TooltipContent>
                        </Tooltip>
                    )}
                </div>

                {isConfigured ? (
                    <div className="mt-3 flex items-start gap-3 rounded-lg border border-amber-700/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-300">
                        <InfoIcon size={18} className="mt-0.5 flex-shrink-0" />
                        <span>{t('preview.deviceBusy')}</span>
                    </div>
                ) : (
                    <>
                        <p className="mt-1 text-xs text-grey-400">
                            {t('preview.layoutNote')}
                        </p>

                        {snapshotError ? (
                            <p className="mt-2 text-sm text-amber-400">
                                {snapshotError}
                            </p>
                        ) : null}
                        {previewError ? (
                            <p className="mt-2 text-sm text-red-400">
                                {previewError}
                            </p>
                        ) : null}

                        <div
                            className="relative mt-4 overflow-hidden rounded-lg border border-grey-700 bg-grey-950"
                            style={{
                                aspectRatio: `${inputWidth} / ${inputHeight}`,
                            }}
                        >
                            {snapshotUrl ? (
                                <img
                                    src={snapshotUrl}
                                    alt={t('preview.snapshotAlt')}
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            ) : (
                                <div className="absolute inset-0 flex items-center justify-center text-grey-600 text-sm">
                                    {t('preview.noSnapshot')}
                                </div>
                            )}

                            <div
                                className="absolute grid"
                                style={{
                                    left: `${crop.left * 100}%`,
                                    top: `${crop.top * 100}%`,
                                    width: `${(crop.right - crop.left) * 100}%`,
                                    height: `${(crop.bottom - crop.top) * 100}%`,
                                    gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
                                    gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                                }}
                            >
                                {preview.map((vp) => (
                                    <div
                                        key={vp.source_id}
                                        className="relative border border-brand-400/40 animate-pulse"
                                    >
                                        <div
                                            className="absolute border border-dashed border-amber-400/60"
                                            style={{
                                                left: `${padding.left * 100}%`,
                                                top: `${padding.top * 100}%`,
                                                right: `${padding.right * 100}%`,
                                                bottom: `${padding.bottom * 100}%`,
                                            }}
                                        />
                                        <span className="absolute left-1.5 top-1.5 rounded-sm bg-grey-900/80 px-1.5 py-0.5 font-mono text-[10px] leading-tight text-grey-100 shadow-sm">
                                            {vp.source_id}
                                        </span>
                                        <span className="absolute bottom-1.5 right-1.5 rounded-sm bg-grey-900/80 px-1.5 py-0.5 text-[10px] leading-tight text-white-100 shadow-sm backdrop-blur-sm">
                                            {vp.width}×{vp.height}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </section>

            <div className="flex flex-wrap gap-3">
                <Button
                    type="button"
                    disabled={applyDisabled}
                    onClick={() => void handleApply()}
                >
                    {t('actions.apply')}
                </Button>
            </div>
        </div>
    );
}
